import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ShortTermPage() {
  const features = [
    {
      icon: 'ri-home-heart-line',
      title: 'Fully Furnished Homes',
      description: 'Every property comes complete with high-quality furniture, linens, kitchenware, and all essentials for a comfortable stay.'
    },
    {
      icon: 'ri-hotel-line',
      title: 'Hotel-Quality Standards',
      description: 'Professional cleaning, fresh linens, and meticulous attention to detail ensure a pristine environment for every guest.'
    },
    {
      icon: 'ri-key-2-line',
      title: 'Secure Self Check-In',
      description: 'Convenient keyless entry systems allow flexible arrival times with secure, contactless access to your rental.'
    },
    {
      icon: 'ri-customer-service-line',
      title: '24/7 Guest Support',
      description: 'Our team is always available to assist with any questions, concerns, or needs throughout your entire stay.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Safe & Monitored',
      description: 'Smart noise monitoring and security systems ensure peaceful stays while protecting the property and neighbors.'
    },
    {
      icon: 'ri-sparkling-line',
      title: 'Professional Maintenance',
      description: 'Regular inspections and prompt repairs keep every property in excellent condition for your comfort and safety.'
    }
  ];

  const amenities = [
    'High-Speed WiFi',
    'Smart TV with Streaming',
    'Full Kitchen',
    'Washer & Dryer',
    'Premium Bedding',
    'Climate Control',
    'Free Parking',
    'Work-Friendly Space',
    'Coffee & Tea',
    'Hair Dryer',
    'Iron & Board',
    'Cleaning Supplies'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section 
        className="relative bg-cover bg-center h-96 flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/1bee6ce86ace816b0699419975f54253.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Short-Term Rentals</h1>
          <p className="text-2xl text-gray-100 max-w-3xl">
            Experience the comfort of home with the service of a hotel
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Our Short-Term Rentals</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you're traveling for business, vacation, or temporary relocation, our properties offer the perfect blend of comfort, convenience, and quality
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-blue-600 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">What to Expect</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Seamless Booking Process</h3>
                    <p className="text-gray-600">Easy online booking with instant confirmation and clear communication before your arrival.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Pristine Cleanliness</h3>
                    <p className="text-gray-600">Professional cleaning between every guest with hospital-grade sanitization and fresh linens.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Respectful Community</h3>
                    <p className="text-gray-600">Clear house rules ensure peaceful stays that respect neighbors and maintain property quality.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Responsive Management</h3>
                    <p className="text-gray-600">Quick response times to any issues or requests, ensuring your stay is comfortable and stress-free.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20furnished%20bedroom%20with%20comfortable%20queen%20bed%2C%20white%20linens%20with%20colorful%20accent%20pillows%2C%20bedside%20lamps%2C%20vibrant%20minimalist%20decor%2C%20warm%20lighting%2C%20hotel%20quality%2C%20clean%20and%20inviting%2C%20professional%20rental%20photography%2C%20welcoming%20atmosphere&width=500&height=600&seq=arm-bedroom-v2&orientation=portrait"
                alt="Bedroom"
                className="rounded-xl shadow-lg object-cover w-full h-64"
              />
              <img 
                src="https://readdy.ai/api/search-image?query=Contemporary%20kitchen%20with%20stainless%20steel%20appliances%2C%20granite%20countertops%2C%20modern%20cabinets%20with%20colorful%20backsplash%2C%20fully%20equipped%2C%20bright%20and%20clean%2C%20professional%20rental%20space%2C%20vibrant%20welcoming%20design&width=500&height=600&seq=arm-kitchen-v2&orientation=portrait"
                alt="Kitchen"
                className="rounded-xl shadow-lg object-cover w-full h-64 mt-8"
              />
              <img 
                src="https://readdy.ai/api/search-image?query=Stylish%20bathroom%20with%20modern%20fixtures%2C%20clean%20white%20tiles%20with%20colorful%20accents%2C%20fresh%20towels%2C%20well-lit%20mirror%2C%20spa-like%20atmosphere%2C%20hotel%20quality%20amenities%2C%20vibrant%20welcoming%20design&width=500&height=600&seq=arm-bathroom-v2&orientation=portrait"
                alt="Bathroom"
                className="rounded-xl shadow-lg object-cover w-full h-64"
              />
              <img 
                src="https://readdy.ai/api/search-image?query=Comfortable%20living%20room%20with%20modern%20sofa%2C%20colorful%20throw%20pillows%2C%20entertainment%20center%2C%20natural%20light%2C%20cozy%20atmosphere%2C%20work-friendly%20space%20with%20desk%20area%2C%20vibrant%20welcoming%20decor&width=500&height=600&seq=arm-living-v2&orientation=portrait"
                alt="Living Room"
                className="rounded-xl shadow-lg object-cover w-full h-64 mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Standard Amenities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every ARM short-term rental includes these essential amenities and more
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {amenities.map((amenity, index) => (
              <div key={index} className="flex items-center space-x-3 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 hover:shadow-md transition-all duration-300">
                <i className="ri-checkbox-circle-fill text-2xl text-teal-600"></i>
                <span className="text-gray-900 font-medium">{amenity}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-teal-600 via-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Book Your Stay?</h2>
          <p className="text-xl text-teal-50 mb-10">
            Experience the ARM difference with our professionally managed short-term rentals. Contact us today to find your perfect temporary home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:Staying.arm@gmail.com"
              className="px-8 py-4 bg-white text-teal-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </a>
            <Link 
              to="/landlords"
              className="px-8 py-4 bg-purple-800 text-white font-semibold rounded-lg hover:bg-purple-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              List Your Property
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
